import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Seo from "../components/Seo";
import { resolveImageUrl } from "../admin/ImageUpload";
import { useCart } from "../context/CartContext";
import { ShoppingBag, Loader2, PackageX, Check } from "lucide-react";
import { toast } from "sonner";

export const fmtEur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;

const Boutique = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const { addItem } = useCart();

  useEffect(() => {
    api.get("/shop/products")
      .then(({ data }) => setProducts(data || []))
      .catch(() => toast.error("Impossible de charger la boutique"))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => Array.from(new Set(products.map((p) => p.category).filter(Boolean))), [products]);
  const shown = category ? products.filter((p) => p.category === category) : products;

  const quickAdd = (p) => {
    const v = (p.variants || []).find((x) => (x.stock || 0) > 0);
    if (!v) return;
    addItem(p, v, 1);
    toast.success(`${p.name} ajouté au panier`);
  };

  return (
    <div data-testid="shop-page">
      <Seo title="Boutique" description="Les goodies de l'école de danse L'Élan Danse : t-shirts, sacs, accessoires. Retrait à l'école." />

      <section className="bg-blush-light/40 border-b border-blush">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 sm:py-20">
          <p className="text-xs uppercase tracking-[0.3em] text-deep-rose mb-3">L&apos;Élan Danse</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-stone-800 leading-tight">La boutique</h1>
          <p className="text-stone-600 mt-4 max-w-xl text-base">
            Portez les couleurs de l&apos;école. Commande en ligne, paiement sécurisé, <strong>retrait à l&apos;école</strong> aux horaires de cours.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-12 sm:py-16">
        {categories.length > 0 && (
          <div className="flex gap-2 flex-wrap mb-8">
            <button onClick={() => setCategory("")} data-testid="shop-cat-all" className={`px-4 py-1.5 rounded-full text-sm transition-colors ${!category ? "bg-deep-rose text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>Tout</button>
            {categories.map((c) => (
              <button key={c} onClick={() => setCategory(c)} data-testid={`shop-cat-${c}`} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${category === c ? "bg-deep-rose text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>{c}</button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="py-16 text-center text-stone-400"><Loader2 className="animate-spin inline" size={22} /></div>
        ) : shown.length === 0 ? (
          <div className="py-16 text-center" data-testid="shop-empty">
            <PackageX size={32} className="mx-auto text-stone-300 mb-3" />
            <p className="text-stone-500 italic">La boutique sera bientôt garnie. Revenez vite&nbsp;!</p>
          </div>
        ) : (
          <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3" data-testid="shop-grid">
            {shown.map((p) => (
              <article key={p.id} className="group" data-testid={`shop-product-${p.id}`}>
                <Link to={`/boutique/${p.id}`} className="block overflow-hidden rounded-2xl bg-stone-100 aspect-[4/5] relative">
                  {(p.images || [])[0] ? (
                    <img src={resolveImageUrl(p.images[0])} alt={p.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-stone-300"><ShoppingBag size={40} /></div>
                  )}
                  {!p.inStock && (
                    <span className="absolute top-3 left-3 bg-stone-900/80 text-white text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full">Épuisé</span>
                  )}
                </Link>
                <div className="mt-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="font-serif-display text-base md:text-lg text-stone-800 truncate">
                      <Link to={`/boutique/${p.id}`} className="hover:text-deep-rose transition-colors">{p.name}</Link>
                    </h2>
                    {p.category ? <p className="text-xs uppercase tracking-widest text-stone-400 mt-0.5">{p.category}</p> : null}
                  </div>
                  <span className="text-deep-rose font-medium shrink-0">{fmtEur(p.price)}</span>
                </div>
                <button
                  onClick={() => quickAdd(p)}
                  disabled={!p.inStock}
                  data-testid={`shop-add-${p.id}`}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-stone-300 hover:border-deep-rose hover:text-deep-rose text-stone-700 px-4 py-2.5 rounded-full text-sm tracking-widest uppercase transition-colors disabled:opacity-40 disabled:hover:border-stone-300 disabled:hover:text-stone-700"
                >
                  {p.inStock ? <><ShoppingBag size={14} /> Ajouter</> : <><Check size={14} /> Indisponible</>}
                </button>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Boutique;
